import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import { ButtonLoader, Input, SelectInput, FileUploadForm } from "components/__new/common/form";
import { ReactComponent as IconAngleLeft } from "assets/images/icons/project-icons/angle-left.svg";
import ComplianceHeader from "components/__new/compliance/ComplianceHeader";
import useBackButton from "hooks/useBackButton";
import { toast } from "react-toastify";
import Maybe from "components/__new/common/Maybe";
import useLoginInfo from "hooks/useLoginInfo";
import validate from "utils/validate";
import { identityDocumentLabel, addressDocumentLabel } from "data/documentLabel";
import PageContent from "components/__new/common/PageContent";
import useUploadDocument from "hooks/compliance/useUploadDocument";
import moment from "moment";
import * as complianceActions from "store/entities/compliance/action";
import * as accountActions from "store/auth/account/action";
import { isDDMMYYYDateValid } from 'utils/dateFormat';


const VerifyBusiness = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {goBack} = useBackButton();
    const {userInfo} = useLoginInfo();
    const { uploadFile, uploadLoading, addSingleBusinessDocument } = useUploadDocument();
    const { employerInfoLoading, employerInfo, companyInfoUpdateLoading, companyInfoUpdate } = useSelector((s) => s.auth.account);
    
    
    const [exitAfterSave, setExitAfterSave] = useState(true);
    const [isFormValidated, setIsFormValidated] = useState(false);
    const [form, setForm] = useState({
        businessName: "",
        businessAddress: "",
        identityVerificationType: "",
        identityVerificationDocument: "",
        dob: "",
        addressVerificationType: "",
        addressVerificationDocument: "",
    })
    
    
    const getLoadingStatus = () => {
        if (companyInfoUpdateLoading === true || uploadLoading === true){
            return true;
        }
        return false;
    }
    
    
    const checkFormValidation = (data = form, appendKey, appendValue) => {
        let params = {...data};
        if (appendKey){
            params = {
                ...params,
                [appendKey]: appendValue,
            };
        }
        const errors = validate(params);
        if (errors){
            setIsFormValidated(false);
            // console.log("checkFormValidation", errors);
        }
        else{
            setIsFormValidated(true);
        }
    }
    
    
    const getEmployerInfo = () => {
        if(!employerInfoLoading && (Object.keys(employerInfo).length === 0 || employerInfo.status !== "success")){
            dispatch(accountActions.getEmployerInfo())
        }
    }
    
    
    const saveDraft = () => {
        submitForm(undefined, false);
    }
    
    
    const submitForm = async (e, exit=true) => {
        e?.preventDefault();
        setExitAfterSave(exit);
        
        let businessDocuments = [
            ...employerInfo?.data?.employer?.company?.businessDocuments ?? [],
        ]
        
        const identityVerification = await doFileUploads(form.identityVerificationType, form.identityVerificationDocument);
        if (identityVerification !== ""){
            businessDocuments = addSingleBusinessDocument(businessDocuments, form.identityVerificationType, identityVerification);
        }
        
        const addressVerification = await doFileUploads(form.addressVerificationType, form.addressVerificationDocument);
        if (addressVerification !== ""){
            businessDocuments = addSingleBusinessDocument(businessDocuments, form.addressVerificationType, addressVerification);
        }
        
        const formattedDob = moment(form.dob).format("DD-MM-YYYY");
        const isDobValid = isDDMMYYYDateValid(formattedDob);
        
        if (!isDobValid) {
            toast.error("Invalid Date of birth, please enter a valid date.");
            return;
        }
        
        const paramsCompany = {
            companyName: form.businessName,
            address: form.businessAddress,
            dob: formattedDob,
            businessDocuments,
        }
        dispatch(accountActions.updateCompanyInfo(paramsCompany));
    }
    
    
    const doFileUploads = async (name, file) => {
        if (file !== "" && typeof file !== "string" && file?.lastModified !== undefined){
            const response = await uploadFile(file, name, userInfo?.company?.companyName);
            if (response?.status === "success"){
                return {name, fileUrl: response?.url ?? ""}
            }
            else{
                toast.error(file?.name + " could not be uploaded");
            }
        }
        return "";
    }
    
    
    const getUploadedDocuments = (businessDocuments) => {
        let identityVerificationType = "";
        let identityVerificationDocument = "";
        let addressVerificationType = "";
        let addressVerificationDocument = "";
        if (businessDocuments !== ""){
            for (const name in identityDocumentLabel){
                const data = businessDocuments?.filter(doc => doc?.name === name) ?? [];
                if (data.length > 0){
                    identityVerificationType = name;
                    identityVerificationDocument = data[0].fileUrl;
                    break;
                }
            }
            for (const name in addressDocumentLabel){
                const data = businessDocuments?.filter(doc => doc?.name === name) ?? [];
                if (data.length > 0){
                    addressVerificationType = name;
                    addressVerificationDocument = data[0].fileUrl;
                    break;
                }
            }
        }
        return {identityVerificationType, identityVerificationDocument, addressVerificationType, addressVerificationDocument};
    }
    
    
    useEffect(() => {
        const {isCompliant, status} = userInfo.employer;

        if (isCompliant === true){
            history.push("/compliance/approval/success");
        }
        else if (isCompliant === false && status === "review"){
            history.push("/compliance/unregistered/approval");
        }
        else{
            dispatch(complianceActions.updateComplianceProgress(25));
            getEmployerInfo();
            checkFormValidation();
        }
        // eslint-disable-next-line
    }, [])
    
    
    useEffect(() => {
        if (employerInfoLoading === false && employerInfo.status === "success"){
            const company = employerInfo?.data?.employer?.company ?? "";
            const {identityVerificationType, identityVerificationDocument, addressVerificationType, addressVerificationDocument} = getUploadedDocuments(company?.businessDocuments);
            if (company !== ""){
                const data = {
                    ...form,
                    businessName: company?.companyName ?? "",
                    businessAddress: company?.address ?? "",
                    identityVerificationType,
                    identityVerificationDocument,
                    dob: moment(company?.dob ?? "", "DD-MM-YYYY").format("YYYY-MM-DD") ?? "",
                    addressVerificationType,
                    addressVerificationDocument,
                }
                setForm(data);
                checkFormValidation(data);
            }
        }
        // eslint-disable-next-line
    }, [employerInfoLoading, employerInfo])
    
    
    useEffect(() => {
        if (companyInfoUpdate.status === "success"){
            dispatch(accountActions.resetUpdateCompanyInfo());
            dispatch(accountActions.updateEmployerInfoFE(companyInfoUpdate?.data));
            if (exitAfterSave === true){
                history.push("/compliance/unregistered/verify-bvn");
            }
            else{
                toast.success("Your data has been saved");
            }
        }
        // eslint-disable-next-line
    }, [companyInfoUpdate])
    
    
    return (
        <div>
            
            <DynamicHeadTag
                headerText="Earnipay Compliance"
                headerDescription="Complete each step to access all Earnipay features"
            />
            
            <ComplianceHeader />
            
            <div className="pt-6 px-6 pb-24 sm:pt-10 sm:px-10">
                <div className="max-w-2xl mx-auto box">
                    
                    <div className="flex justify-start">
                        <div onClick={goBack} className="w-max h-8 pr-2 mr-1 flex items-center cursor-pointer">
                            <IconAngleLeft style={{ height: "16px" }} className="w-max pr-3 m-auto" />
                            Back
                        </div>
                    </div>
                    
                    <div className="pb-2 mt-8 border-b">
                        <div className="font-bold text-xl">
                            Verify Business
                        </div>
                        <div className="text-fade text-sm">
                            Please fill in the details to verify your business
                        </div>
                    </div>
                        
                    <PageContent
                        loading={employerInfoLoading}
                        data={employerInfo}
                        loadingText="Fetching business information..."
                        emptyText="Business information not found"
                        errorText="Unable to fetch business information"
                    />

                    <Maybe condition={employerInfoLoading === false && employerInfo?.status === "success"}>
                        <form onSubmit={submitForm} className="mt-8">
                            <Input type="text" label="Business Name" placeholder="Enter Business Name" required={true} value={form.businessName} onChange={(e) => setForm({...form, businessName: e.target.value})} validationName="businessName" checkFormValidation={checkFormValidation} />
                            <Input type="text" label="Business Address" required={true} value={form.businessAddress} onChange={(e) => setForm({...form, businessAddress: e.target.value})} validationName="businessAddress" checkFormValidation={checkFormValidation} />
                            
                            <div className="mb-4">
                                <div className="font-bold">
                                    Personal Information
                                </div>
                                <div className="text-sm text-ep-gray-600">
                                    Please fill in your personal information
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <Input type="text" label="First Name" required={true} value={userInfo?.employer?.firstName} inputClass="capitalize" disabled={true} />
                                <Input type="text" label="Last Name" required={true} value={userInfo?.employer?.lastName} inputClass="capitalize" disabled={true} />
                            </div>
                            
                            <div className="mb-4">
                                <div className="font-bold">
                                    Identity Verification
                                </div>
                                <div className="text-sm text-ep-gray-600">
                                    Please fill in the details and upload supportive documents below
                                </div>
                            </div>
                            
                            <SelectInput label="Document Type" required={true} value={form.identityVerificationType} onChange={(e) => setForm({...form, identityVerificationType: e.target.value})} validationName="identityVerificationType" checkFormValidation={checkFormValidation}>
                                <option value="">Select Type</option>
                                {Object.keys(identityDocumentLabel).map((name, index) =>
                                    <option key={index} value={name}>{identityDocumentLabel[name]}</option>
                                )}
                            </SelectInput>
                            
                            <Maybe condition={form.identityVerificationType !== ""}>
                                <FileUploadForm
                                    data={form.identityVerificationDocument}
                                    onchange={(e) => setForm({...form, identityVerificationDocument: e})}
                                    filetype={["jpg","jpeg","png","pdf"]}
                                    filename="Identity verification document"
                                    validationName="identityVerificationDocument"
                                    checkFormValidation={checkFormValidation}
                                />
                            </Maybe>
                            
                            <Input type="date" label="Date of Birth" max={moment().subtract(18, 'years').format("YYYY-MM-DD")} required={true} value={form.dob} onChange={(e) => setForm({...form, dob: e.target.value})} validationName="dob" checkFormValidation={checkFormValidation} />
                            
                            <div className="mb-4">
                                <div className="font-bold">
                                    Address Verification
                                </div>
                                <div className="text-sm text-ep-gray-600">
                                    Please upload either of these documents such as Utility Bill, Lease of Tenancy Agreement, Reciept showing your Business Address, etc.
                                </div>
                            </div>
                            
                            <SelectInput label="Document Type" required={true} value={form.addressVerificationType} onChange={(e) => setForm({...form, addressVerificationType: e.target.value})} validationName="addressVerificationType" checkFormValidation={checkFormValidation}>
                                <option value="">Select Type</option>
                                {Object.keys(addressDocumentLabel).map((doc) => 
                                    <option key={doc} value={doc.toLowerCase()}>
                                        {addressDocumentLabel[doc]}
                                    </option>
                                )}
                            </SelectInput>
                            
                            <Maybe condition={form.addressVerificationType !== ""}>
                                <FileUploadForm
                                    data={form.addressVerificationDocument}
                                    onchange={(e) => setForm({...form, addressVerificationDocument: e})}
                                    filetype={["jpg","jpeg","png","pdf"]}
                                    filename="Address verification document"
                                    validationName="addressVerificationDocument"
                                    checkFormValidation={checkFormValidation}
                                />
                            </Maybe>
                            
                            
                            <div className="mt-8 flex justify-end space-x-4">
                                <ButtonLoader type="button" onClick={saveDraft} loading={getLoadingStatus() === true && exitAfterSave === false} className="btn btn-transparent-primary" disabled={!isFormValidated}>
                                    Save a Draft
                                </ButtonLoader>
                                <ButtonLoader type="submit" loading={getLoadingStatus() === true && exitAfterSave === true} className="btn btn-ep-primary" disabled={!isFormValidated}>
                                    Continue
                                </ButtonLoader>
                            </div>
                            
                        </form>
                    </Maybe>
                    
                </div>
            </div>
            
        </div>
    )
}


export default VerifyBusiness;